import {
  User,
  Properties,
  Decision,
  FeatureFlagDecision,
  HackleEvent,
  HackleRemoteConfig
} from "../../../core/internal/model/model"

import { InAppMessageBridge } from "../../iam/ui/html/InAppMessageBridge"
import { BrowserHackleClient, PageView } from "../../../hackle/index.browser"

import { PropertyOperations } from "../../property/PropertyOperations"
import { HackleSubscriptionOperations } from "../../subscription/HackleSubscriptionOperations"
import {
  HackleExperiment,
  HackleInAppMessageLink,
  HackleInAppMessageListener,
  HackleInAppMessageView,
  HacklePage
} from "../../../public/model"
import BrowserPropertyProvider from "../../property/BrowserPropertyProvider"
import {
  CloseInAppMessageViewInvocation,
  GetCurrentInAppMessageViewInvocation,
  HackleAppInvocationProcessor,
  HandleInAppMessageViewInvocation,
  InAppMessageEventDto
} from "../invocator/HackleAppInvocationProcessor"
import { WebAppInAppMessageViewDto } from "./HackleWebAppInAppMessage"
import Logger from "../../../core/internal/logger"
import {
  toInAppMessageActionEventDto,
  toInAppMessageHiddenEventDto
} from "../../iam/ui/html/InAppMessageHtmlActionConverter"
import { TimeUtil } from "../../../core/internal/util/TimeUtil"
import { IAM_HIDE_EXPIRATION_MILLIS } from "../../../config"

export class AppWebViewInAppMessageBridge implements InAppMessageBridge {
  constructor(
    private readonly viewId: string,
    private readonly webAppClient: BrowserHackleClient,
    private readonly invocationProcessor: HackleAppInvocationProcessor,
    private readonly browserPropertyProvider: BrowserPropertyProvider
  ) {
    this.openUrl = this.openUrl.bind(this)
    this.trackClick = this.trackClick.bind(this)
    this.handleUrl = this.handleUrl.bind(this)
    this.closeInAppMessage = this.closeInAppMessage.bind(this)
    this.getSessionId = this.getSessionId.bind(this)
    this.getUser = this.getUser.bind(this)
    this.setUser = this.setUser.bind(this)
    this.setUserId = this.setUserId.bind(this)
    this.setDeviceId = this.setDeviceId.bind(this)
    this.setUserProperty = this.setUserProperty.bind(this)
    this.setUserProperties = this.setUserProperties.bind(this)
    this.updateUserProperties = this.updateUserProperties.bind(this)
    this.updatePushSubscriptions = this.updatePushSubscriptions.bind(this)
    this.updateSmsSubscriptions = this.updateSmsSubscriptions.bind(this)
    this.updateKakaoSubscriptions = this.updateKakaoSubscriptions.bind(this)
    this.resetUser = this.resetUser.bind(this)
    this.setPhoneNumber = this.setPhoneNumber.bind(this)
    this.unsetPhoneNumber = this.unsetPhoneNumber.bind(this)
    this.variation = this.variation.bind(this)
    this.variationDetail = this.variationDetail.bind(this)
    this.isFeatureOn = this.isFeatureOn.bind(this)
    this.featureFlagDetail = this.featureFlagDetail.bind(this)
    this.track = this.track.bind(this)
    this.trackPageView = this.trackPageView.bind(this)
    this.setCurrentPage = this.setCurrentPage.bind(this)
    this.remoteConfig = this.remoteConfig.bind(this)
    this.showUserExplorer = this.showUserExplorer.bind(this)
    this.hideUserExplorer = this.hideUserExplorer.bind(this)
    this.fetch = this.fetch.bind(this)
    this.setInAppMessageListener = this.setInAppMessageListener.bind(this)
    this.getDisplayedInAppMessageView = this.getDisplayedInAppMessageView.bind(this)
    this.getExperiment = this.getExperiment.bind(this)
    this.onReady = this.onReady.bind(this)
    this.onInitialized = this.onInitialized.bind(this)
    this.close = this.close.bind(this)
    this.isOptOutTracking = this.isOptOutTracking.bind(this)
    this.setOptOutTracking = this.setOptOutTracking.bind(this)
  }

  /**
   * viewId is a required value. though we use this for stability.
   */
  private resolveViewId(): string | null {
    return this.viewId ?? this.getCurrentInAppMessageView()?.id ?? null
  }

  private getCurrentInAppMessageView(): WebAppInAppMessageViewDto | null {
    const invocation: GetCurrentInAppMessageViewInvocation = {
      command: "getCurrentInAppMessageView",
      browserProperties: this.browserPropertyProvider.getBrowserProperties()
    }

    const result = this.invocationProcessor.process<never, WebAppInAppMessageViewDto>(invocation)
    return result
  }

  private convertToEventDto({
    url,
    elementId
  }: {
    url?: string | HackleInAppMessageLink
    elementId?: string
  }): InAppMessageEventDto {
    return toInAppMessageActionEventDto({ url, elementId })
  }

  private convertToHiddenEventDto(hideDurationMillis: number): InAppMessageEventDto {
    return toInAppMessageHiddenEventDto(hideDurationMillis)
  }

  // --------------------- iam methods ---------------------
  openUrl(url: string | HackleInAppMessageLink): void {
    const viewId = this.resolveViewId()
    if (!viewId) {
      Logger.log.error("Failed to open url because current in-app message view is not found")
      return
    }

    const event = this.convertToEventDto({ url })
    const invocation: HandleInAppMessageViewInvocation = {
      browserProperties: this.browserPropertyProvider.getBrowserProperties(),
      command: "handleInAppMessageView",
      parameters: {
        viewId,
        handleTypes: ["ACTION"],
        event
      }
    }

    this.invocationProcessor.process(invocation)
  }

  trackClick(elementId?: string | undefined): void {
    const viewId = this.resolveViewId()
    if (!viewId) {
      Logger.log.error("Failed to track click because current in-app message view is not found")
      return
    }

    const event = this.convertToEventDto({ elementId })
    const invocation: HandleInAppMessageViewInvocation = {
      browserProperties: this.browserPropertyProvider.getBrowserProperties(),
      command: "handleInAppMessageView",
      parameters: {
        viewId,
        handleTypes: ["TRACK"],
        event
      }
    }

    this.invocationProcessor.process(invocation)
  }

  handleUrl(url: string | HackleInAppMessageLink, elementId?: string | undefined): void {
    const viewId = this.resolveViewId()
    if (!viewId) {
      Logger.log.error("Failed to handle url because current in-app message view is not found")
      return
    }

    const event = this.convertToEventDto({ url, elementId })
    const invocation: HandleInAppMessageViewInvocation = {
      browserProperties: this.browserPropertyProvider.getBrowserProperties(),
      command: "handleInAppMessageView",
      parameters: {
        viewId,
        handleTypes: ["TRACK", "ACTION"],
        event
      }
    }

    this.invocationProcessor.process(invocation)
  }

  private closeInAppMessageView(): void {
    const viewId = this.resolveViewId()
    if (!viewId) {
      Logger.log.error("Failed to close in-app message because current in-app message view is not found")
      return
    }

    const invocation: CloseInAppMessageViewInvocation = {
      browserProperties: this.browserPropertyProvider.getBrowserProperties(),
      command: "closeInAppMessageView",
      parameters: { viewId }
    }

    this.invocationProcessor.process(invocation)
  }

  private hideInAppMessage(hideDurationMillis: number): void {
    const viewId = this.resolveViewId()
    if (!viewId) {
      Logger.log.error("Failed to hide in-app message because current in-app message view is not found")
      return
    }

    const event = this.convertToHiddenEventDto(hideDurationMillis)
    const invocation: HandleInAppMessageViewInvocation = {
      browserProperties: this.browserPropertyProvider.getBrowserProperties(),
      command: "handleInAppMessageView",
      parameters: {
        viewId,
        handleTypes: ["TRACK", "ACTION"],
        event
      }
    }

    this.invocationProcessor.process(invocation)
  }

  closeInAppMessage(): void
  closeInAppMessage(hideOneDayOrClose: boolean): void
  closeInAppMessage(hideDurationMinutes: number): void
  closeInAppMessage(hideDuration?: boolean | number): void {
    switch (typeof hideDuration) {
      case "boolean":
        const hideOneDay = hideDuration
        hideOneDay ? this.hideInAppMessage(IAM_HIDE_EXPIRATION_MILLIS) : this.closeInAppMessageView()
        break
      case "number":
        const hideDurationMinutes = hideDuration
        const hideDurationMillis = TimeUtil.minutesToUnit(hideDurationMinutes, "milliseconds")
        this.hideInAppMessage(hideDurationMillis)
        break
      default:
        this.closeInAppMessageView()
    }
  }

  // --------------------- supported methods ---------------------
  getSessionId(): string {
    return this.webAppClient.getSessionId()
  }
  getUser(): User {
    return this.webAppClient.getUser()
  }
  setUser(user: User): Promise<void> {
    return this.webAppClient.setUser(user)
  }
  setUserId(userId: string | number | null | undefined): Promise<void> {
    return this.webAppClient.setUserId(userId)
  }
  setDeviceId(deviceId: string): Promise<void> {
    return this.webAppClient.setDeviceId(deviceId)
  }
  setUserProperty(key: string, value: any): Promise<void> {
    return this.webAppClient.setUserProperty(key, value)
  }
  setUserProperties(properties: Properties<string>): Promise<void> {
    return this.webAppClient.setUserProperties(properties)
  }
  updateUserProperties(operations: PropertyOperations): Promise<void> {
    return this.webAppClient.updateUserProperties(operations)
  }
  updatePushSubscriptions(operations: HackleSubscriptionOperations): Promise<void> {
    return this.webAppClient.updatePushSubscriptions(operations)
  }
  updateSmsSubscriptions(operations: HackleSubscriptionOperations): Promise<void> {
    return this.webAppClient.updateSmsSubscriptions(operations)
  }
  updateKakaoSubscriptions(operations: HackleSubscriptionOperations): Promise<void> {
    return this.webAppClient.updateKakaoSubscriptions(operations)
  }
  resetUser(): Promise<void> {
    return this.webAppClient.resetUser()
  }
  setPhoneNumber(phoneNumber: string): Promise<void> {
    return this.webAppClient.setPhoneNumber(phoneNumber)
  }
  unsetPhoneNumber(): Promise<void> {
    return this.webAppClient.unsetPhoneNumber()
  }
  variation(experimentKey: number, user?: string | User | undefined, defaultVariation?: string | undefined): string {
    return this.webAppClient.variation(experimentKey, user, defaultVariation)
  }
  variationDetail(
    experimentKey: number,
    user?: string | User | undefined,
    defaultVariation?: string | undefined
  ): Decision {
    return this.webAppClient.variationDetail(experimentKey, user, defaultVariation)
  }
  isFeatureOn(featureKey: number, user?: string | User | undefined): boolean {
    return this.webAppClient.isFeatureOn(featureKey, user)
  }
  featureFlagDetail(featureKey: number, user?: string | User | undefined): FeatureFlagDecision {
    return this.webAppClient.featureFlagDetail(featureKey, user)
  }
  track(event: string | HackleEvent<string>, user?: string | User | undefined): void {
    return this.webAppClient.track(event, user)
  }
  trackPageView(option?: PageView | undefined): void {
    return this.webAppClient.trackPageView(option)
  }
  setCurrentPage(page: HacklePage): void {
    return this.webAppClient.setCurrentPage(page)
  }
  remoteConfig(user?: string | User | undefined): HackleRemoteConfig {
    return this.webAppClient.remoteConfig(user)
  }
  showUserExplorer(): void {
    return this.webAppClient.showUserExplorer()
  }
  hideUserExplorer(): void {
    return this.webAppClient.hideUserExplorer()
  }
  fetch(): Promise<void> {
    return this.webAppClient.fetch()
  }
  setInAppMessageListener(listener: HackleInAppMessageListener | null): void {
    return this.webAppClient.setInAppMessageListener(listener)
  }
  getDisplayedInAppMessageView(): HackleInAppMessageView | null {
    return this.webAppClient.getDisplayedInAppMessageView()
  }
  getExperiment(experimentKey: number): HackleExperiment | undefined {
    return this.webAppClient.getExperiment(experimentKey)
  }
  onReady(block: () => void, timeout?: number | undefined): void {
    return this.webAppClient.onReady(block, timeout)
  }
  onInitialized(config?: { timeout?: number | undefined } | undefined): Promise<{ success: boolean }> {
    return this.webAppClient.onInitialized(config)
  }
  close(): void {
    return this.webAppClient.close()
  }
  isOptOutTracking(): boolean {
    return this.webAppClient.isOptOutTracking()
  }
  setOptOutTracking(optOut: boolean): void {
    return this.webAppClient.setOptOutTracking(optOut)
  }
}
